// src/components/resources/tools/generators/shared/usePostExportPrompt.ts

'use client'

import { useCallback, useState } from 'react'

const DISMISS_PREFIX = 'adsights_post_export_dismiss_'

/**
 * Session-scoped post-export prompt controller. Shows at most once per
 * browser tab session per resource slug unless the user dismisses it.
 *
 * @param {string} resourceSlug - Generator slug used for dismiss scoping.
 * @returns Prompt open state and handlers.
 */
export function usePostExportPrompt(resourceSlug: string): {
  open: boolean
  onOpenChange: (open: boolean) => void
  promptAfterExport: () => void
  dismissForSession: () => void
} {
  const [open, setOpen] = useState(false)
  const dismissKey = `${DISMISS_PREFIX}${resourceSlug}`

  const dismissForSession = useCallback((): void => {
    try {
      sessionStorage.setItem(dismissKey, '1')
    } catch {
      // Storage unavailable — still close the dialog.
    }
    setOpen(false)
  }, [dismissKey])

  const promptAfterExport = useCallback((): void => {
    try {
      if (sessionStorage.getItem(dismissKey) === '1') return
    } catch {
      // If storage is blocked, still show the prompt once per mount.
    }
    setOpen(true)
  }, [dismissKey])

  const onOpenChange = useCallback(
    (next: boolean): void => {
      if (!next) dismissForSession()
      else setOpen(true)
    },
    [dismissForSession],
  )

  return { open, onOpenChange, promptAfterExport, dismissForSession }
}
